import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, LogOut, Inbox, Building2, Clock, MailOpen, Trash2, Reply } from "lucide-react";
import { LOGO_URL } from "../data/content";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05080F] flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <div className="w-full max-w-md bg-[#161B28] border border-white/5 rounded-lg p-8 relative z-10" data-testid="admin-login-card">
        <Link to="/" className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="Skymoon" className="h-14 w-auto" />
        </Link>
        <h1 className="text-2xl font-serif font-bold text-[#F8FAFC] text-center mb-2">Espace Privé</h1>
        <p className="text-sm text-[#94A3B8] text-center mb-8">Boîte de réception des messages</p>
        <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-sm text-[#94A3B8] mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="contact-input"
              placeholder="admin@..."
              data-testid="admin-email"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94A3B8] mb-2">Mot de passe</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="contact-input"
              placeholder="••••••••"
              data-testid="admin-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Connexion..." : "Se Connecter"}
          </button>
          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}

function buildReplyHref(msg) {
  const subject = encodeURIComponent("Re: Votre message à Skymoon Consultancy");
  const body = encodeURIComponent(
    `Bonjour ${msg.name},\n\nMerci pour votre message :\n\n> ${msg.message.replace(/\n/g, "\n> ")}\n\n\n\nCordialement,\nSkymoon Consultancy\nDubai · Dakar\ncontact@skymoonconsultancy.com`
  );
  return `mailto:${msg.email}?subject=${subject}&body=${body}`;
}

function MessageCard({ msg, index, onToggleRead, onDelete }) {
  return (
    <div
      className={`bg-[#161B28] border rounded-lg p-6 transition-all ${msg.read ? "border-white/5 opacity-75" : "border-[#C5A059]/40"}`}
      data-testid={`admin-message-${index + 1}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          {!msg.read && <span className="w-2.5 h-2.5 shrink-0 bg-[#C5A059] rounded-full" data-testid={`admin-unread-dot-${index + 1}`} />}
          <div>
            <h3 className="text-lg font-semibold text-[#F8FAFC]">{msg.name}</h3>
            <a href={`mailto:${msg.email}`} className="text-sm text-[#C5A059] hover:underline">{msg.email}</a>
          </div>
        </div>
        <div className="flex flex-col md:items-end gap-1">
          {msg.organization && (
            <span className="flex items-center gap-1.5 text-sm text-[#94A3B8]">
              <Building2 size={14} /> {msg.organization}
            </span>
          )}
          <span className="flex items-center gap-1.5 text-xs text-[#64748B]">
            <Clock size={12} />
            {new Date(msg.timestamp).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}
          </span>
        </div>
      </div>
      <p className="text-[#94A3B8] leading-relaxed whitespace-pre-wrap border-l-2 border-[#C5A059]/40 pl-4 mb-5">
        {msg.message}
      </p>
      <div className="flex items-center gap-3">
        <a
          href={buildReplyHref(msg)}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#C5A059] text-[#05080F] font-semibold hover:bg-[#D4AF6A] transition-colors"
          data-testid={`admin-reply-${index + 1}`}
        >
          <Reply size={13} />
          Répondre
        </a>
        <button
          onClick={() => onToggleRead(msg)}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
          data-testid={`admin-toggle-read-${index + 1}`}
        >
          {msg.read ? <Mail size={13} /> : <MailOpen size={13} />}
          {msg.read ? "Marquer non lu" : "Marquer comme lu"}
        </button>
        <button
          onClick={() => onDelete(msg)}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
          data-testid={`admin-delete-${index + 1}`}
        >
          <Trash2 size={13} />
          Supprimer
        </button>
      </div>
    </div>
  );
}

function useInbox() {
  const [authed, setAuthed] = useState(null);
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout request failed (session cleared locally):", err);
    }
    setAuthed(false);
    setMessages(null);
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/contact`, { withCredentials: true });
      setMessages(data);
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchMessages();
  }, [authed, fetchMessages]);

  const toggleRead = useCallback(async (msg) => {
    try {
      await axios.patch(`${API}/api/contact/${msg.id}/read`, { read: !msg.read }, { withCredentials: true });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: !msg.read } : m)));
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    }
  }, []);

  const deleteMessage = useCallback(async (msg) => {
    if (!window.confirm(`Supprimer définitivement le message de ${msg.name} ?`)) return;
    try {
      await axios.delete(`${API}/api/contact/${msg.id}`, { withCredentials: true });
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    }
  }, []);

  return { authed, setAuthed, messages, logout, toggleRead, deleteMessage };
}

export default function AdminPage() {
  const { authed, setAuthed, messages, logout, toggleRead, deleteMessage } = useInbox();

  useEffect(() => {
    const unread = (messages || []).filter((m) => !m.read).length;
    document.title = unread > 0
      ? `(${unread}) Boîte de Réception | Skymoon Consultancy`
      : "Boîte de Réception | Skymoon Consultancy";
  }, [messages]);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#05080F] flex items-center justify-center">
        <p className="text-[#94A3B8]" data-testid="admin-loading">Chargement...</p>
      </div>
    );
  }

  if (!authed) {
    return <LoginForm onSuccess={() => setAuthed(true)} />;
  }

  const unreadCount = (messages || []).filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-[#05080F]">
      <div className="noise-overlay" />
      <nav className="nav-fixed nav-scrolled">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Skymoon" className="h-12 w-auto" />
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#C5A059] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={16} />
              Déconnexion
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-20 relative z-10" data-testid="admin-inbox">
        <div className="flex items-center gap-3 mb-2">
          <Inbox className="text-[#C5A059]" size={24} />
          <h1 className="text-3xl font-serif font-bold text-[#F8FAFC]">Boîte de Réception</h1>
        </div>
        <p className="text-[#94A3B8] mb-10" data-testid="admin-message-count">
          {messages === null
            ? "Chargement..."
            : `${messages.length} message${messages.length > 1 ? "s" : ""} · ${unreadCount} non lu${unreadCount > 1 ? "s" : ""}`}
        </p>

        {messages !== null && messages.length === 0 && (
          <div className="bg-[#161B28] border border-white/5 rounded-lg p-12 text-center" data-testid="admin-empty-state">
            <Mail className="text-[#C5A059] mx-auto mb-4" size={32} />
            <p className="text-[#94A3B8]">Aucun message pour le moment.</p>
          </div>
        )}

        <div className="space-y-6">
          {(messages || []).map((msg, i) => (
            <MessageCard
              key={msg.id}
              msg={msg}
              index={i}
              onToggleRead={toggleRead}
              onDelete={deleteMessage}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
